import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { Box, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { requestUpdatingProfile } from "../../redux/actions/userActionCreators";
import { useMediaQuery } from "@mui/material";
import Badge from "@mui/material/Badge";
import Camera from "../../assets/camera-plus.svg";
import { styled } from "@mui/material/styles";
import AvatarModal from "./AvatarModal";
import { requestUpdateCreditTiers } from "../../redux/actions/tabManagementActionCreators";

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 22,
  height: 22,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
}));

const TIER_KEYS = ["tier_1", "tier_2", "tier_3"];
const COST_KEYS = [
  "flash_report",
  "risk_report_without_validation",
  "risk_report_with_validation",
];

const EMPTY_TIER_RULES = {
  tier_1: {
    flash_report: "",
    risk_report_without_validation: "",
    risk_report_with_validation: "",
  },
  tier_2: {
    flash_report: "",
    risk_report_without_validation: "",
    risk_report_with_validation: "",
  },
  tier_3: {
    flash_report: "",
    risk_report_without_validation: "",
    risk_report_with_validation: "",
  },
};

const normalizeGlobalTierRules = (rawRules) => {
  const normalized = JSON.parse(JSON.stringify(EMPTY_TIER_RULES));
  if (!rawRules || typeof rawRules !== "object") {
    return normalized;
  }

  TIER_KEYS.forEach((tierKey) => {
    const tierPayload = rawRules?.[tierKey];
    if (!tierPayload || typeof tierPayload !== "object") {
      return;
    }

    COST_KEYS.forEach((costKey) => {
      const parsedValue = Number(tierPayload?.[costKey]);
      if (Number.isFinite(parsedValue) && parsedValue >= 0) {
        normalized[tierKey][costKey] = parsedValue;
      }
    });
  });

  return normalized;
};

export default function AlertDialog({ open, handleClose, profile }) {
  const { t } = useTranslation();
  const storedUser = localStorage.getItem("user");
  let user = {};
  if (storedUser) {
    try {
      user = JSON.parse(storedUser);
    } catch (e) {
      user = {};
    }
  }

  const [openModal, setOpenModal] = useState(false);
  const [avatarSeleceted, setAvatarsSeleceted] = useState(user?.avatar_url || "");

  const dispatch = useDispatch();

  const handleSelect = (selected) => {
    setAvatarsSeleceted(selected);
    setOpenModal(false);
  };

  const isMobile = useMediaQuery("(max-width:1400px)");
  const isEnterpriseAccount = profile?.account_type === "enterprise";
  const isSuperAdmin = profile?.role === "cytailor_admin";
  const hasGlobalTierRules =
    isSuperAdmin &&
    profile?.credit_tiers &&
    typeof profile.credit_tiers === "object" &&
    TIER_KEYS.some(
      (tierKey) =>
        profile?.credit_tiers?.[tierKey] &&
        typeof profile.credit_tiers[tierKey] === "object"
    );
  const selectedTier = profile?.credit_tier;
  const remainingCredits = profile?.credit_remaining;
  const reservedCredits = profile?.credit_reserved;
  const tierCosts = profile?.credit_tier_costs || {};
  const parsedRemainingCredits = Number(remainingCredits);
  const parsedReservedCredits = Number(reservedCredits);
  const safeRemainingCredits =
    Number.isFinite(parsedRemainingCredits) && parsedRemainingCredits >= 0
      ? parsedRemainingCredits
      : 0;
  const safeReservedCredits =
    Number.isFinite(parsedReservedCredits) && parsedReservedCredits >= 0
      ? parsedReservedCredits
      : 0;
  const remainingAfterReports = Math.max(
    safeRemainingCredits - safeReservedCredits,
    0
  );
  const hasCreditData =
    isEnterpriseAccount &&
    selectedTier &&
    remainingCredits !== null &&
    remainingCredits !== undefined;
  const hasZeroCurrentCredits = safeRemainingCredits === 0;
  const showReservedCreditBlock = safeRemainingCredits > 0;
  const showRemainingCreditBlock =
    safeRemainingCredits > 0 && safeReservedCredits > 0;
  const visibleCreditBlockCount =
    1 + (showReservedCreditBlock ? 1 : 0) + (showRemainingCreditBlock ? 1 : 0);
  const currentCreditMdSize =
    visibleCreditBlockCount === 1
      ? 4
      : visibleCreditBlockCount === 2
      ? 6
      : 3.9;
  const reservedCreditMdSize = visibleCreditBlockCount === 3 ? 3.9 : 6;
  const remainingCreditMdSize = visibleCreditBlockCount === 3 ? 4.1 : 6;
  const [globalTierRules, setGlobalTierRules] = useState(EMPTY_TIER_RULES);
  const showContactForLowCredits = hasCreditData && remainingAfterReports === 0;

  useEffect(() => {
    if (!hasGlobalTierRules) {
      setGlobalTierRules(JSON.parse(JSON.stringify(EMPTY_TIER_RULES)));
      return;
    }
    setGlobalTierRules(normalizeGlobalTierRules(profile?.credit_tiers));
  }, [hasGlobalTierRules, profile?.credit_tiers]);

  const handleGlobalTierRuleChange = useCallback((tierKey, costKey, value) => {
    setGlobalTierRules((prev) => {
      if (value === "") {
        return {
          ...prev,
          [tierKey]: {
            ...prev[tierKey],
            [costKey]: "",
          },
        };
      }
      const parsedValue = Number(value);
      if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        return prev;
      }
      return {
        ...prev,
        [tierKey]: {
          ...prev[tierKey],
          [costKey]: parsedValue,
        },
      };
    });
  }, []);

  const canSaveGlobalTierRules = TIER_KEYS.every((tierKey) =>
    COST_KEYS.every((costKey) => {
      const parsed = Number(globalTierRules?.[tierKey]?.[costKey]);
      return Number.isFinite(parsed) && parsed >= 0;
    })
  );
  const formik = useFormik({
    initialValues: {
      firstName: profile?.first_name,
      lastName: profile?.last_name,
      email: profile?.email,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required(t("required_field")),
      lastName: Yup.string().required(t("required_field")),
      email: Yup.string()
        .email(t("valid_email"))
        .max(255)
        .required(t("required_field")),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const normalizedProfileFirstName = String(profile?.first_name || "").trim();
        const normalizedProfileLastName = String(profile?.last_name || "").trim();
        const normalizedIncomingFirstName = String(values.firstName || "").trim();
        const normalizedIncomingLastName = String(values.lastName || "").trim();
        const normalizedProfileAvatar = profile?.avatar_url || user?.avatar_url || "";
        const normalizedIncomingAvatar = avatarSeleceted || "";

        const shouldUpdateProfile =
          normalizedIncomingFirstName !== normalizedProfileFirstName ||
          normalizedIncomingLastName !== normalizedProfileLastName ||
          normalizedIncomingAvatar !== normalizedProfileAvatar;

        const normalizedCurrentTierRules = normalizeGlobalTierRules(profile?.credit_tiers);
        const normalizedIncomingTierRules = normalizeGlobalTierRules(globalTierRules);
        const shouldUpdateTierRules =
          hasGlobalTierRules &&
          JSON.stringify(normalizedIncomingTierRules) !==
            JSON.stringify(normalizedCurrentTierRules);

        if (shouldUpdateTierRules) {
          const payload = {
            tiers: TIER_KEYS.reduce((acc, tierKey) => {
              acc[tierKey] = COST_KEYS.reduce((costAcc, costKey) => {
                costAcc[costKey] = Number(globalTierRules?.[tierKey]?.[costKey]);
                return costAcc;
              }, {});
              return acc;
            }, {}),
          };
          await dispatch(requestUpdateCreditTiers(payload));
        }

        if (shouldUpdateProfile) {
          user.first_name = values.firstName;
          user.last_name = values.lastName;
          user.avatar_url = avatarSeleceted;
          localStorage.setItem("user", JSON.stringify(user));

          await dispatch(
            requestUpdatingProfile(
              values.firstName,
              values.lastName,
              avatarSeleceted
            )
          );
        }

        resetForm({ values });
        handleClose();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const tierRuleRows = [
    {
      key: "flash_report",
      label: t("flash_report_credit_cost"),
    },
    {
      key: "risk_report_without_validation",
      label: t("risk_report_without_validation_cost"),
    },
    {
      key: "risk_report_with_validation",
      label: t("risk_report_with_validation_cost"),
    },
  ];
  const creditCostRows = [
    {
      key: "flash_report",
      label: t("flash_report_credit_cost"),
      value: tierCosts?.flash_report,
    },
    {
      key: "risk_report_without_validation",
      label: t("risk_report_without_validation_cost"),
      value: tierCosts?.risk_report_without_validation,
    },
    {
      key: "risk_report_with_validation",
      label: t("risk_report_with_validation_cost"),
      value: tierCosts?.risk_report_with_validation,
    },
  ].filter((row) => row.value !== undefined && row.value !== null);

  const closeModal = () => {
    setOpenModal(false);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => handleClose()}
        maxWidth="xl"
        PaperProps={{
          sx: !isMobile
            ? { width: "35%", padding: "20px " }
            : { width: "90%", height: "90%", margin: "5px 0", padding: "5px " },
        }}
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          <IconButton onClick={() => handleClose()}>
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle mb={3}>
            <Typography variant="title4">{t("edit_profile")}</Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <Badge
                  overlap="circular"
                  onClick={() => setOpenModal(true)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                    backgroundColor: "#FFFFFF",
                  }}
                  badgeContent={<SmallAvatar alt="Remy Sharp" src={Camera} />}
                >
                  <Avatar
                    alt="Remy Sharp"
                    src={avatarSeleceted}
                    sx={{
                      width: !isMobile ? "140px" : "70px",
                      height: !isMobile ? "140px" : "70px",
                    }}
                  />
                </Badge>
              </Grid>
              <Grid item xs={8} align="left">
                <Stack>
                  <Typography variant="title4">{`${profile?.first_name} ${profile?.last_name}`}</Typography>
                  <Typography variant="subtitle5" color="#00000066">
                    {profile?.account_type === "free"
                      ? profile?.entreprise === profile?.organization_name
                        ? `${
                            profile?.role === "enterprise_user"
                              ? profile?.entreprise
                              : profile?.organization_name
                          }`
                        : `${
                            profile?.role === "admin"
                              ? t("admin")
                              : profile?.role === "enterprise_user"
                              ? t("enterprise_user")
                              : t("user")
                          } at ${
                            profile?.role === "enterprise_user"
                              ? profile?.entreprise
                              : profile?.organization_name
                          }`
                      : `${t("admin")} at ${profile?.entreprise}`}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            <Grid container mt={4} spacing={3}>
              <Grid item lg={12} xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("surname")}
                </Typography>

                <TextField
                  id="outlined-basic"
                  placeholder={t("surname")}
                  name="firstName"
                  variant="outlined"
                  fullWidth
                  error={Boolean(
                    formik.touched.firstName && formik.errors.firstName
                  )}
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.firstName}
                />
              </Grid>
              <Grid item lg={12} xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("name")}
                </Typography>
                <TextField
                  id="outlined-basic"
                  placeholder={t("name")}
                  name="lastName"
                  variant="outlined"
                  fullWidth
                  error={Boolean(
                    formik.touched.lastName && formik.errors.lastName
                  )}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.lastName}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("email_address")}
                </Typography>
                <TextField
                  id="outlined-basic"
                  placeholder={t("email_address")}
                  variant="outlined"
                  name="email"
                  fullWidth
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  disabled
                />
              </Grid>
              {isEnterpriseAccount && !isSuperAdmin && hasCreditData && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2.25 },
                      borderRadius: { xs: "14px", sm: "18px" },
                      border: "1px solid #DBE2EA",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <Box
                      sx={{
                        mb: { xs: 1.15, sm: 1.4 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ letterSpacing: "0.01em" }}>
                          {t("credit_plan_information")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 1.2,
                          py: 0.45,
                          borderRadius: "999px",
                          border: "1px solid #C9D5E3",
                          backgroundColor: "#F9FBFD",
                        }}
                      >
                        <Typography variant="subtitle7" sx={{ color: "#334155", fontWeight: 700 }}>
                          {t(selectedTier)}
                        </Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                      <Grid item xs={12} md={currentCreditMdSize}>
                        <Box
                          sx={{
                            p: { xs: 1.3, sm: 1.45 },
                            borderRadius: "14px",
                            border: hasZeroCurrentCredits
                              ? "1px solid #F5B5B5"
                              : "1px solid #B7E4CF",
                            backgroundColor: hasZeroCurrentCredits
                              ? "#FFF7F7"
                              : "#F4FBF7",
                            minHeight: "50%",
                          }}
                        >
                          <Typography
                            variant="subtitle7"
                            sx={{
                              color: hasZeroCurrentCredits ? "#991B1B" : "#0F5132",
                              minHeight: "2.8em",
                              display: "flex",
                              alignItems: "flex-start",
                              lineHeight: 1.35,
                            }}
                          >
                            {t("initial_credits", { defaultValue: "Current Credits" })}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.2,
                              fontSize: { xs: "1.8rem", sm: "2rem" },
                              fontWeight: 700,
                              lineHeight: 1,
                              color: hasZeroCurrentCredits ? "#7F1D1D" : "#0B3D2E",
                            }}
                          >
                            {safeRemainingCredits}
                          </Typography>
                        </Box>
                      </Grid>
                      {showReservedCreditBlock && (
                        <Grid item xs={12} md={reservedCreditMdSize}>
                          <Box
                            sx={{
                              p: { xs: 1.3, sm: 1.45 },
                              borderRadius: "14px",
                              backgroundColor: "#FFFAF5",
                              border: "1px solid #F5D1A6",
                              minHeight: "50%",
                            }}
                          >
                            <Typography
                              variant="subtitle7"
                              color="#9A3412"
                              sx={{
                                minHeight: "2.8em",
                                display: "flex",
                                alignItems: "flex-start",
                                lineHeight: 1.35,
                              }}
                            >
                              {t("reserved_credits", { defaultValue: "Reserved Credits" })}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.2,
                                fontSize: { xs: "1.8rem", sm: "2rem" },
                                fontWeight: 700,
                                lineHeight: 1,
                                color: "#7C2D12",
                              }}
                            >
                              {safeReservedCredits}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {showRemainingCreditBlock && (
                        <Grid item xs={12} md={remainingCreditMdSize}>
                          <Box
                            sx={{
                              p: { xs: 1.3, sm: 1.45 },
                              borderRadius: "14px",
                              border: showContactForLowCredits
                                ? "1px solid #F5B5B5"
                                : "1px solid #C7D2FE",
                              backgroundColor: showContactForLowCredits
                                ? "#FFF7F7"
                                : "#EEF4FF",
                              minHeight: "50%",
                            }}
                          >
                            <Typography
                              variant="subtitle7"
                              sx={{
                                color: showContactForLowCredits ? "#991B1B" : "#334155",
                                minHeight: "2.8em",
                                display: "flex",
                                alignItems: "flex-start",
                                lineHeight: 1.35,
                              }}
                            >
                              {t("remaining_after_reports_generation", {
                                defaultValue: "Remaining Credits After Report Generation",
                              })}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.2,
                                fontSize: { xs: "1.8rem", sm: "2rem" },
                                fontWeight: 700,
                                lineHeight: 1,
                                color: showContactForLowCredits ? "#7F1D1D" : "#1E3A8A",
                              }}
                            >
                              {remainingAfterReports}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                    <Box
                      sx={{
                        mt: { xs: 1.2, sm: 1.4 },
                        p: { xs: 1.1, sm: 1.35 },
                        borderRadius: "12px",
                        backgroundColor: "#FAFCFF",
                        border: "1px solid #E5EAF1",
                      }}
                    >
                      <Typography
                        variant="subtitle7"
                        sx={{ color: "#64748B", mb: 0.55, fontWeight: 700, letterSpacing: "0.01em" }}
                      >
                        Report Credit Costs
                      </Typography>
                      {creditCostRows.map((row) => (
                        <Box
                          key={row.key}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            alignItems: "center",
                            columnGap: 1,
                            py: 0.62,
                          }}
                        >
                          <Typography variant="subtitle7">{row.label}</Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              minWidth: "30px",
                              textAlign: "right",
                              lineHeight: 1.2,
                            }}
                          >
                            {row.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {!showContactForLowCredits && (
                      <Box
                        sx={{
                          mt: { xs: 1, sm: 1.25 },
                          p: { xs: 1.15, sm: 1.3 },
                          borderRadius: "10px",
                          border: "1px solid #C7D2FE",
                          backgroundColor: "#EEF4FF",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <InfoOutlinedIcon sx={{ color: "#4F46E5", fontSize: 18, mt: "2px" }} />
                        <Typography
                          variant="subtitle4"
                          sx={{ color: "#1E3A8A", lineHeight: 1.55 }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            If you do not have enough credits to generate a report, please
                            contact admin@cytailor.io to request additional credits.
                          </Box>
                        </Typography>
                      </Box>
                    )}
                    {showContactForLowCredits && (
                      <Box
                        sx={{
                          mt: 1.1,
                          p: { xs: 1.2, sm: 1.35 },
                          borderRadius: "10px",
                          border: "1px solid #F59E0B",
                          backgroundColor: "#FFF7ED",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ color: "#EA580C", fontSize: 19, mt: "2px" }} />
                        <Typography
                          variant="subtitle4"
                          sx={{ color: "#7C2D12", lineHeight: 1.55 }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            Insufficient available credits.
                          </Box>{" "}
                          You cannot generate a report right now. Please contact{" "}
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            admin@cytailor.io
                          </Box>{" "}
                          to request additional credits.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
              {hasGlobalTierRules && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #E5E7EB" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      {t("global_tier_rules")}
                    </Typography>
                    <Grid
                      container
                      spacing={1.25}
                      sx={{ mb: 0.75, display: { xs: "none", md: "flex" } }}
                    >
                      <Grid item md={3}>
                        <Typography variant="subtitle4" color="#6B7280">
                          Report Type
                        </Typography>
                      </Grid>
                      {TIER_KEYS.map((tierKey) => (
                        <Grid item md={3} key={`header-${tierKey}`}>
                          <Typography variant="subtitle4" color="#6B7280">
                            {t(tierKey)}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                    {tierRuleRows.map((row) => (
                      <Grid
                        container
                        spacing={1.25}
                        alignItems="center"
                        sx={{ mb: 1.25 }}
                        key={row.key}
                      >
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle7">{row.label}</Typography>
                        </Grid>
                        {TIER_KEYS.map((tierKey) => (
                          <Grid item xs={12} md={3} key={`${row.key}-${tierKey}`}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{ min: 0 }}
                              label={isMobile ? t(tierKey) : undefined}
                              value={globalTierRules?.[tierKey]?.[row.key] ?? ""}
                              onChange={(event) =>
                                handleGlobalTierRuleChange(
                                  tierKey,
                                  row.key,
                                  event.target.value
                                )
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="button"
              type="submit"
              fullWidth
              disabled={formik.isSubmitting || (hasGlobalTierRules && !canSaveGlobalTierRules)}
              sx={{ margin: "15px 15px" }}
            >
              {t("save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <AvatarModal
        open={openModal}
        handleClose={closeModal}
        handleSelect={handleSelect}
      />
    </div>
  );
}
